/**
 * Register this install on login and heartbeat on foreground.
 * Reuses getDeviceId() (same id as call Switch / activeDeviceId).
 */

import { App } from '@capacitor/app'
import { useEffect, useRef } from 'react'
import { useAuth } from '../../../contexts/auth-context'
import {
  heartbeatMyDevice,
  isAppSyncConfigured,
  registerMyDevice,
} from '../../../services/appsync-client'
import { isBiometricUnlockEnabled } from '../../../services/biometric-auth-service'
import {
  isDeviceVerificationError,
  isStillRegisteringError,
} from '../../../services/device-install-secret'
import { getDeviceId, primeDeviceId } from '../../../utils/device-id'
import { beginDeviceSessionCheck, resetDeviceSessionCheck } from '../../../stores/device-session-store'
import {
  deviceReportForSync,
  markDeviceReportSent,
  onDeviceReportNow,
} from '../../setup/data/setup-report-store'
import { isNativeApp } from '../../../utils/platform'
import {
  buildAutoDeviceDisplayName,
  coarseLocationHint,
  resolveDevicePlatform,
} from '../utils/device-display-name'

const HEARTBEAT_MIN_INTERVAL_MS = 60_000

let lastHeartbeatAt = 0

export async function collectRegistrationPayload() {
  await primeDeviceId()
  const deviceId = getDeviceId()
  const biometricEnrolled = await isBiometricUnlockEnabled().catch(() => false)
  return {
    deviceId,
    platform: resolveDevicePlatform(),
    displayName: buildAutoDeviceDisplayName(),
    appVersion: '1.0.0',
    biometricEnrolled,
    approxLocation: coarseLocationHint(),
  }
}

async function handleRevokedLocally(signOut: () => Promise<void>): Promise<void> {
  // signOut() clears the soft-lock clocks itself, after the session teardown
  // is confirmed. If it fails the clocks stay armed (fail-closed) and the
  // next heartbeat retries the revoked → sign-out path.
  try {
    await signOut()
  } catch (err) {
    console.warn('[devices] revoked sign-out failed; will retry on next heartbeat', err)
  }
}

/**
 * Auto-register session on login; heartbeat lastSeen on foreground.
 * If the server reports revoked, clear soft-lock clocks and sign out locally.
 */
export function useDeviceRegistry() {
  const { user, signOut } = useAuth()
  const signOutRef = useRef(signOut)
  signOutRef.current = signOut

  useEffect(() => {
    if (!user?.userId || !isAppSyncConfigured()) return

    let cancelled = false

    const userId = user.userId
    const sync = async (mode: 'register' | 'heartbeat', opts: { force?: boolean } = {}) => {
      const now = Date.now()
      if (mode === 'heartbeat' && !opts.force && now - lastHeartbeatAt < HEARTBEAT_MIN_INTERVAL_MS) return
      // Push registration waits for this answer (see device-session-store).
      const endCheck = beginDeviceSessionCheck()
      try {
        const payload = await collectRegistrationPayload()
        if (cancelled) return
        // Setup report: only when it changed, daily, or right after a setup change.
        const report = deviceReportForSync(userId, now, { force: opts.force })
        const reportFields = report ? { permissions: report.permissions, setup: report.setup } : {}
        const device =
          mode === 'register'
            ? await registerMyDevice({ ...payload, ...reportFields })
            : await heartbeatMyDevice({
                deviceId: payload.deviceId,
                biometricEnrolled: payload.biometricEnrolled,
                approxLocation: payload.approxLocation,
                appVersion: payload.appVersion,
                ...reportFields,
              }).catch(async (err) => {
                // First heartbeat before register finished, cold race, or a rejected secret —
                // fall back to register. A client that holds no secret for its id is moved to a
                // NEW device id by registerMyDevice (the old row is never re-claimed).
                const message = err instanceof Error ? err.message : String(err)
                if (/not found/i.test(message) || isDeviceVerificationError(err)) {
                  return registerMyDevice({ ...payload, ...reportFields })
                }
                throw err
              })
        lastHeartbeatAt = Date.now()
        if (report) markDeviceReportSent(userId, report.fingerprint, lastHeartbeatAt)
        if (device.status === 'revoked') {
          endCheck('signed-out')
          await handleRevokedLocally(() => signOutRef.current())
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        // Another call from this install is mid-claim; the next sync picks it up.
        if (isStillRegisteringError(err)) return
        if (isDeviceVerificationError(err)) {
          // The server rejected this install's secret and this install DOES hold one (a
          // lost secret is handled in registerMyDevice by moving to a new device id). Nothing
          // was changed on the server; don't loop, the next foreground retries.
          console.warn('[devices] the server could not verify this device', err)
          return
        }
        if (/signed out remotely|DEVICE_REVOKED|revoked/i.test(message)) {
          endCheck('signed-out')
          await handleRevokedLocally(() => signOutRef.current())
          return
        }
        console.warn('[devices] registry sync failed', err)
      } finally {
        endCheck('live')
      }
    }

    void sync('register')
    const removeReportNow = onDeviceReportNow(() => void sync('heartbeat', { force: true }))

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void sync('heartbeat')
    }
    document.addEventListener('visibilitychange', onVisibility)

    let removeNative: (() => void) | undefined
    if (isNativeApp()) {
      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void sync('heartbeat')
      }).then((handle) => {
        removeNative = () => {
          void handle.remove()
        }
      })
    }

    return () => {
      cancelled = true
      resetDeviceSessionCheck()
      removeReportNow()
      document.removeEventListener('visibilitychange', onVisibility)
      removeNative?.()
    }
  }, [user?.userId])
}
